import { useMemo } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { useDevice, useProductCapabilities } from '@/hooks';
import { isSmokeCoZone, isTemperatureZone, isMiscSirenZone, isMiscNoSirenZone, alarmTypeIconPath } from '@/lib/enum-resolvers';
import { computeMetrics, statusColors } from '@/lib/sensor-metrics';
import { isIntegratedSensor } from '@/lib/sensor-map';
import { wirelessSensorOnlineState } from '@/lib/sensor-commands';
import type { NASensorDataObject, NASensorMapObject } from '@/api/models';

interface NavItem {
  label: string;
  to: string;
  icon: string;
  // Same yellow/red-only-when-flagged convention as the ApplicationsPage
  // dashboard cards this mirrors -- omitted (not 'normal') means no dot.
  status?: 'warning' | 'danger';
}

interface NavSection {
  section: string;
  items: NavItem[];
}

const ICONS = {
  devices: 'M4 6h16M4 10h16M4 14h16M4 18h16',
  fleet: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7',
  tracking: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z',
  security: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  apps: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm0 8a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zm12 0a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z',
  geofence: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z',
  intrusion: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
  bilge: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z',
  switch: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4',
  dc: 'M13 10V3L4 14h7v7l9-11h-7z',
  trends: 'M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z',
  settings: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
  contacts: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
  logs: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  comms: 'M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.566 14.587-5.566 20.152 0M12 20.25h.008v.008H12v-.008z',
  account: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  // Reuse the same shapes as the per-zone icons (alarmTypeIconPath) so a sidebar
  // entry matches the icon shown on its dashboard card and zone tiles.
  smokeCo: alarmTypeIconPath(4),
  temperature: alarmTypeIconPath(10),
  miscSiren: alarmTypeIconPath(31),
  misc: alarmTypeIconPath(30),
  sensors: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z',
} as const;

const globalNav: NavItem[] = [
  { label: 'My Devices', to: '/devices', icon: ICONS.devices },
  { label: 'Fleet Map', to: '/fleet', icon: ICONS.fleet },
];

// 'normal' -> no dot at all, so the nav only calls out what actually needs
// attention (same convention the ApplicationsPage cards use for their dot).
function dotStatus(s: 'normal' | 'warning' | 'danger'): 'warning' | 'danger' | undefined {
  return s === 'normal' ? undefined : s;
}

function buildDeviceNav(
  deviceUUID: string,
  sensorData: NASensorDataObject | null | undefined,
  sensorMapList: NASensorMapObject[] | null | undefined,
  supportsWirelessExpansion: boolean,
): NavSection[] {
  const metrics = computeMetrics(sensorData);
  const hasDC = (sensorData?.dcSensorList?.length ?? 0) > 0;
  const hasAC = (sensorData?.acSensorList?.length ?? 0) > 0;
  const allZones = sensorData?.securitySensorlist ?? [];
  const hasSecurity = allZones.length > 0;
  const hasSmokeCo = allZones.some((z) => isSmokeCoZone(z));
  const hasTemperature = allZones.some((z) => isTemperatureZone(z));
  const hasMiscSiren = allZones.some((z) => isMiscSirenZone(z));
  const hasMisc = allZones.some((z) => isMiscNoSirenZone(z));
  const hasNevata = (sensorData?.nevataSensorList?.length ?? 0) > 0;
  const hasGeofence = (sensorData?.geofenceList?.length ?? 0) > 0;
  // Water zones from security sensor list count toward bilge
  const hasWaterZones = (sensorData?.securitySensorlist ?? []).some(
    (z) => (z.alarmType as unknown) === 9 || (z.alarmType as unknown) === 27 || z.alarmType === 'ALARM_TYPE_WATER' || z.alarmType === 'ALARM_TYPE_ZONE_WATER_V2'
  );
  // Switches only shown if not reserved for arm/disarm control
  const switchReserved = (sensorData?.securitySensorlist ?? []).some(
    (z) => z.zoneInstance === 0 && ((z.alarmType as unknown) === 32 || (z.alarmType as unknown) === 'ALARM_TYPE_ZONE_GEOFENCE_ARM_DISARM_VTRACKER')
  );
  const hasSwitch = (sensorData?.switchSensorList?.length ?? 0) > 0 && !switchReserved;

  // Any paired wireless sensor currently offline -- flagged app-wide via the
  // Sensors nav item's dot, per the request to make offline sensors "clear
  // to the user" without having to open the page.
  const wirelessSensors = (sensorMapList ?? []).filter((s) => !isIntegratedSensor(s));
  const hasOfflineWirelessSensor = wirelessSensors.some(
    (s) => wirelessSensorOnlineState(s, allZones, sensorData?.nevataSensorList ?? []) === 'Offline',
  );

  // Build applications list dynamically
  const appItems: NavItem[] = [
    { label: 'Overview', to: `/device/${deviceUUID}/applications`, icon: ICONS.apps },
  ];

  if (hasGeofence) {
    appItems.push({ label: 'Device Geofence', to: `/device/${deviceUUID}/device-geofence`, icon: ICONS.geofence, status: dotStatus(metrics.geofenceStatus) });
  }
  // No status dot -- being outside a boundary is expected/normal for a
  // vessel underway, not an alert worth flagging app-wide in the nav.
  appItems.push({ label: 'Global Geofences', to: `/device/${deviceUUID}/global-geofences`, icon: ICONS.geofence });
  if (hasSecurity) {
    appItems.push({ label: 'Intrusion Zones', to: `/device/${deviceUUID}/intrusion`, icon: ICONS.intrusion, status: dotStatus(metrics.securityStatus) });
  }
  if (hasSmokeCo) {
    appItems.push({ label: 'Smoke/CO', to: `/device/${deviceUUID}/smoke-co`, icon: ICONS.smokeCo, status: dotStatus(metrics.smokeCoStatus) });
  }
  if (hasTemperature) {
    appItems.push({ label: 'Temperature', to: `/device/${deviceUUID}/temperature`, icon: ICONS.temperature, status: dotStatus(metrics.temperatureStatus) });
  }
  if (hasMiscSiren) {
    appItems.push({ label: 'Misc & Siren', to: `/device/${deviceUUID}/misc-siren`, icon: ICONS.miscSiren, status: dotStatus(metrics.miscSirenStatus) });
  }
  if (hasMisc) {
    appItems.push({ label: 'Misc', to: `/device/${deviceUUID}/misc`, icon: ICONS.misc, status: dotStatus(metrics.miscStatus) });
  }
  if (hasSwitch) {
    // Switches are plain on/off controls, not alarm-bearing sensors -- no
    // status dot, matching mobile (which has no alert concept for them either).
    appItems.push({ label: 'Switches', to: `/device/${deviceUUID}/switches`, icon: ICONS.switch });
  }
  if (hasNevata || hasWaterZones) {
    appItems.push({ label: 'Bilge / Float', to: `/device/${deviceUUID}/bilge`, icon: ICONS.bilge, status: dotStatus(metrics.bilgeStatus) });
  }
  if (hasDC) {
    appItems.push({ label: 'DC Banks', to: `/device/${deviceUUID}/dc-banks`, icon: ICONS.dc, status: dotStatus(metrics.dcStatus) });
  }
  if (hasAC) {
    appItems.push({ label: 'AC Shore Power', to: `/device/${deviceUUID}/ac-power`, icon: ICONS.dc, status: dotStatus(metrics.acStatus) });
  }

  return [
    {
      section: 'Device',
      items: [
        { label: 'Tracking', to: `/device/${deviceUUID}/tracking`, icon: ICONS.tracking },
        { label: 'Security', to: `/device/${deviceUUID}/security`, icon: ICONS.security },
        { label: 'Communications', to: `/device/${deviceUUID}/communications`, icon: ICONS.comms },
        { label: 'Device Info', to: `/device/${deviceUUID}/info`, icon: ICONS.info },
      ],
    },
    {
      section: 'Applications',
      items: appItems,
    },
    {
      section: 'Configuration',
      items: [
        { label: 'Settings', to: `/device/${deviceUUID}/configuration`, icon: ICONS.settings },
        ...(supportsWirelessExpansion
          ? [
              {
                label: 'Sensors',
                to: `/device/${deviceUUID}/sensors`,
                icon: ICONS.sensors,
                status: hasOfflineWirelessSensor ? ('danger' as const) : undefined,
              },
            ]
          : []),
        { label: 'Contacts', to: `/device/${deviceUUID}/contacts`, icon: ICONS.contacts },
      ],
    },
    {
      section: 'Logs',
      items: [
        { label: 'Event Log', to: `/device/${deviceUUID}/logs`, icon: ICONS.logs },
      ],
    },
  ];
}

function NavIcon({ path }: { path: string }) {
  return (
    <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}

export function Sidebar() {
  const { deviceUUID } = useParams();
  const { selectedDeviceInfo } = useDevice();
  const caps = useProductCapabilities();

  const deviceNav = useMemo(() => {
    if (!deviceUUID) return [];
    return buildDeviceNav(deviceUUID, selectedDeviceInfo?.sensorData, selectedDeviceInfo?.sensorMapList, caps.supportsWirelessExpansion);
  }, [deviceUUID, selectedDeviceInfo?.sensorData, selectedDeviceInfo?.sensorMapList, caps.supportsWirelessExpansion]);

  return (
    <aside className="flex h-full w-60 flex-col border-r border-slate-200 bg-slate-50 dark:border-navy-700 dark:bg-navy-900">
      <nav className="flex-1 overflow-y-auto px-3 py-4 scrollbar-thin scrollbar-dark">
        {/* Global navigation */}
        <div className="mb-4">
          <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            Overview
          </p>
          {globalNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                clsx('nav-link mb-0.5', isActive && 'nav-link-active')
              }
            >
              <NavIcon path={item.icon} />
              {item.label}
            </NavLink>
          ))}
        </div>

        {/* Device-specific navigation (dynamic) */}
        {deviceUUID &&
          deviceNav.map((group) => (
            <div key={group.section} className="mb-4">
              <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                {group.section}
              </p>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    clsx('nav-link mb-0.5', isActive && 'nav-link-active')
                  }
                >
                  <NavIcon path={item.icon} />
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.status && (
                    <span
                      className={clsx('h-2 w-2 shrink-0 rounded-full', statusColors[item.status].dot)}
                      title={item.status === 'danger' ? 'Needs attention' : 'Warning'}
                    />
                  )}
                </NavLink>
              ))}
            </div>
          ))}
      </nav>

      {/* Account link at bottom */}
      <div className="border-t border-slate-200 px-3 py-3 dark:border-navy-700">
        <NavLink
          to="/account"
          className={({ isActive }) =>
            clsx('nav-link', isActive && 'nav-link-active')
          }
        >
          <NavIcon path={ICONS.account} />
          Account
        </NavLink>
      </div>
    </aside>
  );
}
